import { useMemo } from 'react';
import { Action, Subject } from '@erp/authz';
import { authzClient } from '@/context/authz/AuthzClient';
import { RouteItem } from '@/routes';

export function getPermissionKey(action: string, subject: string): string {
  return `${action}:${subject}`;
}

type PermissionCheck = { action: Action; subject: Subject };

function extractUniquePermissions(routes: RouteItem[]): PermissionCheck[] {
  const permissionMap = new Map<string, PermissionCheck>();

  function collectPermissions(routeList: RouteItem[]) {
    for (const route of routeList) {
      if (route.permissionsAnyOf) {
        for (const perm of route.permissionsAnyOf) {
          const key = getPermissionKey(perm.action, perm.subject);
          if (!permissionMap.has(key)) {
            permissionMap.set(key, {
              action: perm.action,
              subject: perm.subject,
            });
          }
        }
      }

      if (route.permissionsAllOf) {
        for (const perm of route.permissionsAllOf) {
          const key = getPermissionKey(perm.action, perm.subject);
          if (!permissionMap.has(key)) {
            permissionMap.set(key, {
              action: perm.action,
              subject: perm.subject,
            });
          }
        }
      }

      if (route.collapse) {
        collectPermissions(route.collapse);
      }
    }
  }

  collectPermissions(routes);
  return Array.from(permissionMap.values());
}

/**
 * Hook to check route permissions from the pre-populated authz cache.
 *
 * The cache is populated synchronously on login via initializeFromAbilityRules,
 * so this hook reads synchronously without any loading state.
 */
export function useRoutePermissions(routes: RouteItem[]): {
  permissions: Map<string, boolean>;
  isLoading: boolean;
  error: Error | null;
} {
  const permissions = useMemo(() => {
    const uniquePermissions = extractUniquePermissions(routes);
    const permissionMap = new Map<string, boolean>();

    for (const perm of uniquePermissions) {
      const key = getPermissionKey(perm.action, perm.subject);
      const allowed = authzClient.getCachedPermission(
        perm.action,
        perm.subject,
      );
      // If not in cache, default to false (no permission)
      permissionMap.set(key, allowed ?? false);
    }

    return permissionMap;
  }, [routes]);

  return {
    permissions,
    isLoading: false,
    error: null,
  };
}
